import { Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { PatientService } from '../../services/patient.service';
import { PatientAuthorizationService } from '../../services/patient-authorization.service';
import { GlobalErrorHandlerService } from '../../services/global-error.service';
import { Patient } from '../../health/doctor/patient';

// Reusable "search the whole of Wellwink for a patient, and gate unauthorized
// ones behind SMS/KBA" field — same flow as the Walk-In / Emergency Arrive
// "Patient*" field, packaged so any screen can drop it in.
//
// Two ways to use it:
//   1. Interactive — the user types and picks a result from the dropdown.
//   2. Programmatic — a parent already has a patient_id from the backend
//      (an inbound message, a reply, a C-CDA match, an add-to-chart auto-fill)
//      and calls verifyPatientId() to silently confirm authorization, only
//      surfacing the modal if that patient actually isn't linked yet.
//
// Deliberately does NOT use PatientAuthorizationService's mutable fields
// (verified / unathorizedPatient / type / data) — only its pure helpers
// (isAuthorized, highlightQuery) — because DSM can have several of these
// instances alive at once (multiple compose windows, a panel + a search box),
// and a shared singleton's flow state would let concurrent gates clobber each
// other. PatientAuthorizationService itself is untouched, so Walk-In/Arrive/
// Navsearch (which only ever have one instance on screen) are unaffected.
@Component({
  selector: 'app-patient-authorize-search',
  templateUrl: './patient-authorize-search.component.html',
  // The host itself must never generate a layout box: several call sites keep
  // this component permanently mounted (even while visually "inactive", via
  // [hideField]) so its @ViewChild + authorization modal are always available —
  // display:contents means an inactive instance (input hidden, modal display:none
  // by default) takes up literally no space, instead of leaving an empty flex/
  // block box that would compete for room with whatever sibling content the
  // parent shows in its place (e.g. compose's "Loading patient…" / patient badge).
  host: { style: 'display: contents' },
})
export class PatientAuthorizeSearchComponent implements OnDestroy {
  @Input() placeholder = 'Search Patient';
  @Input() inputClass = 'form-control';
  @Input() name = 'patientAuthorizeSearch';
  // Hides just the input box (e.g. while a parent shows its own "loading" /
  // "confirmed" state instead). Never hides the authorization modal — see
  // the template — so a verifyPatientId()/gatePatient() call in flight can
  // always pop it regardless of whether the field itself is visible.
  @Input() hideField = false;
  // Emits the authorized patient record ({id, name, complete_detail, ...})
  // once a selection (manual or programmatic) clears the authorization gate.
  @Output() patientSelected = new EventEmitter<any>();
  // Emits true while a verify-by-id check (or the modal it may open) is in
  // flight, false once it settles — parents can use this to disable actions
  // (Reply, Add to Chart, Send) until the gate resolves.
  @Output() authorizationPending = new EventEmitter<boolean>();
  // Emits when the user cancels the authorization modal instead of verifying.
  @Output() authorizationCancelled = new EventEmitter<any>();
  // Optional demographics (e.g. from a C-CDA match) to prefill the "Add New
  // Patient" form with when no search result is the right patient.
  @Input() addPatientPrefill: { first_name?: string; last_name?: string; dob?: string; gender?: string } = null;

  @ViewChild('authorizationModal', { static: false }) authorizationModal: ModalDirective;
  @ViewChild('addPatientModal', { static: false }) addPatientModal: ModalDirective;

  asyncSelected = '';
  dataSourcePatients: Observable<any>;
  typeaheadLoading = false;
  typeaheadNoResults = false;

  // --- Per-instance authorization-flow state ---
  pendingPatient: any = null;
  authType: 'sms' | 'kba' | '' = '';
  verified = true;
  otp = '';
  motherName = '';
  hint = '';
  messageClass = '';
  mobileMessage = '';
  generalMessage = '';
  isSendingOtp = false;
  isVerifying = false;

  private resolvePending: (patient: any) => void = null;
  private rejectPending: (reason?: any) => void = null;
  private clearMessagesTimer: any;

  // --- "Add New Patient" (no search result is the right one) ---
  // Same canonical add-patient form used on the patient listing page
  // (DemographicsComponent), so quick-adds go through identical validation.
  newPatient: Patient = new Patient();

  constructor(
    private patientService: PatientService,
    public patientAuthService: PatientAuthorizationService,
    private globalErrorHandler: GlobalErrorHandlerService,
  ) {
    this.dataSourcePatients = Observable.create((observer: any) => {
      observer.next(this.asyncSelected);
    }).pipe(mergeMap((token: string) => this.patientService.getPatientAsObservable(token)));
  }

  ngOnDestroy(): void {
    clearTimeout(this.clearMessagesTimer);
    // A pending caller (verifyPatientId) must not hang forever if this
    // instance is destroyed mid-flow (e.g. parent panel closed).
    if (this.rejectPending) { this.rejectPending('destroyed'); }
  }

  changeTypeaheadLoading(e: boolean): void {
    this.typeaheadLoading = e;
  }

  changeTypeaheadNoResults(e: boolean): void {
    this.typeaheadNoResults = e;
  }

  isAuthorized(patient: any): boolean {
    return this.patientAuthService.isAuthorized(patient);
  }

  highlightQuery(str: string, query: string[]): any {
    return this.patientAuthService.highlightQuery(str, query);
  }

  typeaheadOnSelect(e: any): void {
    const patient = e.item;
    this.gate(patient)
      .then(authorized => {
        this.asyncSelected = '';
        this.typeaheadNoResults = false;
        this.patientSelected.emit(authorized);
      })
      .catch(reason => {
        // ngx-bootstrap's typeahead writes the picked item's complete_detail into
        // asyncSelected the instant it's chosen from the dropdown — before the
        // gate even settles. If the user then cancels the authorization modal,
        // that text is left behind, making the field look like this (never
        // actually attached) patient is still selected. Clear it so the box
        // honestly reflects "nothing selected" and is ready for another search.
        //
        // Exception: 'superseded' means a *newer* selection already took over
        // this instance (picked a second patient before the first's modal was
        // resolved) — that selection now legitimately owns asyncSelected (its
        // own name), so clearing it here would wipe out the newer pick instead.
        if (reason !== 'superseded') {
          this.asyncSelected = '';
        }
      });
  }

  // Public API for parents: verify an already-known patient_id without a
  // fresh text search. Resolves with the authorized patient, or null if the
  // id doesn't resolve to a real patient. Never rejects (safe to .then()
  // without a .catch()) — a user cancel resolves null, same as "not found".
  verifyPatientId(patientId: number): Promise<any> {
    if (!patientId) { return Promise.resolve(null); }
    this.authorizationPending.emit(true);
    return this.patientService.getPatientByIdGlobal(patientId)
      .then(patient => {
        if (!patient) {
          this.authorizationPending.emit(false);
          return null;
        }
        return this.gate(patient)
          .then(authorized => {
            this.authorizationPending.emit(false);
            return authorized;
          })
          .catch(() => {
            this.authorizationPending.emit(false);
            return null;
          });
      })
      .catch(() => {
        this.authorizationPending.emit(false);
        return null;
      });
  }

  // Public API for parents that already have a full patient record in hand
  // (e.g. a C-CDA demographic match, or a "linked" patient from the backend)
  // and just need it run through the authorization gate — no extra fetch.
  // Rejects if the user cancels, so callers can tell "not authorized yet,
  // still pending" apart from "confirmed" — unlike verifyPatientId(), which
  // is meant to be used silently and so swallows cancellation into null.
  gatePatient(patient: any): Promise<any> {
    if (!patient) { return Promise.reject('no-patient'); }
    return this.gate(patient);
  }

  // Resolves immediately if the patient already belongs to one of the
  // current user's practices; otherwise opens the modal and resolves only
  // once SMS/KBA verification succeeds. Rejects if the user cancels.
  private gate(patient: any): Promise<any> {
    if (this.isAuthorized(patient)) {
      return Promise.resolve(patient);
    }
    // A prior pending gate on this same instance (shouldn't normally happen —
    // one search box, one selection at a time) is treated as superseded.
    if (this.rejectPending) { this.rejectPending('superseded'); }

    this.pendingPatient = patient;
    this.hint = patient.mother_name_hint;
    this.authType = '';
    this.verified = false;
    this.otp = '';
    this.motherName = '';
    this.messageClass = '';
    this.mobileMessage = '';
    this.generalMessage = '';
    this.authorizationModal.show();
    return new Promise((resolve, reject) => {
      this.resolvePending = resolve;
      this.rejectPending = reject;
    });
  }

  chooseAuthType(type: 'sms' | 'kba'): void {
    this.authType = type;
    if (type === 'sms') { this.sendOtp(); }
  }

  sendOtp(): void {
    if (!this.pendingPatient || this.isSendingOtp) { return; }
    this.isSendingOtp = true;
    this.patientService.sendOtpForPatientAuthorization({
      patient_id: this.pendingPatient.id,
      type: 'sms',
      data: '',
    }).then(response => {
      this.isSendingOtp = false;
      this.generalMessage = response?.message || 'Message has been sent.';
      this.messageClass = 'success';
      this.scheduleClearMessages();
    }).catch(res => {
      this.isSendingOtp = false;
      this.globalErrorHandler.error = res?.error?.error || res?.error?.message;
      this.generalMessage = res?.error?.message || res?.message || 'Failed to send verification message.';
      this.messageClass = 'failed';
      this.scheduleClearMessages();
    });
  }

  verifyOtp(): void {
    if (!this.pendingPatient || this.isVerifying || !this.otp) { return; }
    this.isVerifying = true;
    this.patientService.verifyPatientAuthorizationOtp({
      patient_id: this.pendingPatient.id,
      otp: this.otp,
    }).then(res => {
      this.isVerifying = false;
      this.mobileMessage = res?.message || 'Verification Code verified';
      this.messageClass = 'success';
      this.verified = true;
      this.scheduleClearMessages();
    }).catch(res => {
      this.isVerifying = false;
      this.mobileMessage = res?.error?.message || res?.message || 'Verification failed.';
      this.messageClass = 'failed';
      this.scheduleClearMessages();
    });
  }

  verifyMotherName(): void {
    if (!this.pendingPatient || this.isVerifying || !this.motherName) { return; }
    this.isVerifying = true;
    this.patientService.verifyPatientMotherName({
      patient_id: this.pendingPatient.id,
      mother_name: this.motherName,
    }).then(res => {
      this.isVerifying = false;
      this.mobileMessage = res?.message || 'Mother Name verified.';
      this.messageClass = 'success';
      this.verified = true;
      this.scheduleClearMessages();
    }).catch(res => {
      this.isVerifying = false;
      this.mobileMessage = res?.error?.message || res?.message || 'Verification failed.';
      this.messageClass = 'failed';
      this.scheduleClearMessages();
    });
  }

  resendOtp(): void {
    this.sendOtp();
  }

  // "Continue" — only enabled once verified is true.
  onContinue(): void {
    if (!this.verified || !this.pendingPatient) { return; }
    const patient = this.pendingPatient;
    this.authorizationModal.hide();
    const resolve = this.resolvePending;
    this.resetPending();
    if (resolve) { resolve(patient); }
  }

  // "Cancel" (or the × / backdrop) — always available.
  hideModal(): void {
    this.authorizationModal.hide();
    const wasPending = !!this.rejectPending;
    const reject = this.rejectPending;
    this.resetPending();
    if (reject) { reject('cancelled'); }
    if (wasPending) { this.authorizationCancelled.emit(); }
  }

  private resetPending(): void {
    this.pendingPatient = null;
    this.authType = '';
    this.verified = true;
    this.otp = '';
    this.motherName = '';
    this.resolvePending = null;
    this.rejectPending = null;
  }

  private scheduleClearMessages(): void {
    clearTimeout(this.clearMessagesTimer);
    this.clearMessagesTimer = setTimeout(() => {
      this.mobileMessage = '';
      this.generalMessage = '';
    }, 3500);
  }

  // Parents call this to clear the search box (e.g. after the user hits
  // "Change patient" / "Remove"), without needing to know internal state.
  reset(): void {
    this.asyncSelected = '';
    this.typeaheadNoResults = false;
    this.typeaheadLoading = false;
  }

  // Search came up empty (or wrong) — offer to create the patient instead.
  // Prefilled from addPatientPrefill when the caller has demographics in
  // hand (e.g. a C-CDA document); otherwise opens blank.
  openAddPatient(): void {
    this.newPatient = new Patient();
    const p = this.addPatientPrefill;
    if (p) {
      if (p.first_name) { this.newPatient.user.first_name = p.first_name; }
      // User.last_name is mistyped `number` on the model — cast around it here.
      if (p.last_name)  { (this.newPatient.user as any).last_name = p.last_name; }
      if (p.dob)        { this.newPatient.user.dob = p.dob; this.newPatient.user.dob_stage = '0'; }
      if (p.gender)     { this.newPatient.user.gender = p.gender; }
    }
    this.addPatientModal.show();
  }

  closeAddPatient(): void {
    this.addPatientModal.hide();
  }

  // The patient was just created by this practice's staff, so they're
  // inherently authorized for it — but re-fetch through verifyPatientId()
  // rather than trusting the create response directly, so the emitted
  // record has the same shape (complete_detail, patient_practices_id,
  // mother_name_hint) every other patientSelected consumer expects.
  onNewPatientCreated(patient: any): void {
    this.closeAddPatient();
    const id = patient?.id;
    if (!id) { return; }
    this.verifyPatientId(id).then(authorized => {
      if (authorized) { this.patientSelected.emit(authorized); }
    });
  }
}
